// Detect e-ink display via standard signals. Result is stable across page
// lifetime (won't change between calls), so callers can safely cache.
//
// Escape hatches when auto-detect misses a device:
//   1. URL query param ?eink=1 — shareable link override, STICKY: persisted
//      to localStorage so the device stays in e-ink mode on later visits
//      without the param. ?eink=0 reverts (also sticky, suppresses
//      auto-detect — the way out of a false positive).
//   2. localStorage 'ev_force_eink' ('true'/'false') — what the param writes;
//      support can also set it directly via DevTools.
// Known e-ink browsers / brand fragments — caught when the manufacturer
// keeps them in the UA (or Client Hints model string).
const EINK_BRAND_RE = /EinkBro|NeoBrowser|ONYX|BOOX|Kindle|Silk|Kobo|reMarkable|PocketBook|iReader|InkBook|Tolino|Likebook|MEEBOOK/i;

// BOOX Build.MODEL codenames (NeoBrowser strips brand names; only the model
// survives in the UA device slot, e.g. "Linux; Android 11; Leaf3").
// Two hard rules, learned from real collisions:
//   - Families that mainstream phones reuse (Note, Nova) REQUIRE their
//     distinctive suffix immediately after the family name — a bare
//     `\bNote\b` would flag every "Redmi Note 12" / "HUAWEI nova 9" as
//     e-ink and force paged grayscale UI on them.
//   - Each branch ends in `\w*` so variant suffixes keep matching (color
//     "C", generation digits after "Air": NoteAir4C, TabUltraC, Tab10C).
// Keep alphabetically sorted; new models: add here AND in eink.test.js.
const BOOX_MODEL_RE = /\b(?:Faust\d*|Go(?:Color)?\d{1,3}\w*|Leaf[1-9]?C?|Lumi\d*|Max\d*Lumi\w*|Mira\w*|Note(?:[1-9]|Air|X|Max)\w*|Nova(?:[1-9]|Air|C)\w*|Page\w*|Palma\w*|Poke[1-9S]\w*|Tab(?:\d|Ultra|Mini|X)\w*)\b/i;

export function detectEink() {
  try {
    // Explicit overrides first — param wins over stored, stored wins over
    // auto-detect. Both directions are sticky.
    if (typeof location !== 'undefined') {
      const param = new URLSearchParams(location.search).get('eink');
      if (param === '1' || param === 'true') {
        try { localStorage.setItem('ev_force_eink', 'true'); } catch { /* private mode */ }
        return true;
      }
      if (param === '0' || param === 'false') {
        try { localStorage.setItem('ev_force_eink', 'false'); } catch { /* private mode */ }
        return false;
      }
    }
    const stored = localStorage.getItem('ev_force_eink');
    if (stored === 'true') return true;
    if (stored === 'false') return false;

    // CSS media query — the official e-ink signal. Modern Chromium honors it.
    if (window.matchMedia('(update: slow)').matches) return true;

    // Low color depth — e-ink is 1-bit (mono) or 4-bit grayscale. Modern
    // LCD/OLED is always ≥ 24-bit, so ≤ 8 is a strong e-ink indicator.
    if (window.screen.colorDepth <= 8) return true;

    // Known e-ink browsers / device UA fragments. Two layers:
    //   (a) brand names — caught when manufacturer keeps them in UA.
    //   (b) BOOX device model codes — NeoBrowser (BOOX's default browser)
    //       strips brand names and only leaves the Android Build.MODEL in
    //       the device portion of the UA (e.g., "Linux; Android 11; Leaf3").
    //       We enumerate known BOOX models because matchMedia('update: slow')
    //       and screen.colorDepth aren't reliable on BOOX's Chromium fork.
    const ua = navigator.userAgent;
    if (EINK_BRAND_RE.test(ua)) return true;
    if (BOOX_MODEL_RE.test(ua)) return true;
  } catch {
    // ignore
  }
  return false;
}

// Chrome 110+ UA Reduction replaces the device model in userAgent with a
// literal "K", blinding codename sniffing on up-to-date browsers (BOOX's
// NeoBrowser included, once its Chromium base catches up). The true model
// is still exposed via Client Hints — but only asynchronously, so this
// runs AFTER the sync detect in main.jsx: on a match it flips
// <html data-eink> (CSS reacts immediately; JS gates like the Kindle
// selection mode pick it up on the next chapter mount) and persists the
// force flag so every later load takes the sync fast path.
export async function refineEinkViaClientHints() {
  try {
    if (isEinkActive()) return;
    // Explicit user opt-out (?eink=0) beats auto-refinement.
    if (localStorage.getItem('ev_force_eink') === 'false') return;
    const uad = navigator.userAgentData;
    if (!uad?.getHighEntropyValues) return;
    const { model } = await uad.getHighEntropyValues(['model']);
    if (!model) return;
    if (BOOX_MODEL_RE.test(model) || EINK_BRAND_RE.test(model)) {
      document.documentElement.setAttribute('data-eink', 'true');
      applyPagedTransformFlag();
      try { localStorage.setItem('ev_force_eink', 'true'); } catch { /* private mode */ }
    }
  } catch { /* Client Hints unsupported — sync detect already did its best */ }
}

// transform 分页实现启用:墨水屏默认开(BOOX 系统手势劫持的架构性
// 根治——容器不可滚,手势层无物可认领);逃生口 ev_paged_transform='0'
// 同时决定 attr(CSS overflow)与 pagedImpl(位移实现),二者必须同源,
// 否则"CSS 隐藏溢出但位移仍走 scrollLeft"会白屏第 2 页起的内容。
export function applyPagedTransformFlag() {
  try {
    const enabled = isEinkActive() && localStorage.getItem('ev_paged_transform') !== '0';
    if (enabled) document.documentElement.setAttribute('data-paged-transform', 'true');
    else document.documentElement.removeAttribute('data-paged-transform');
  } catch { /* SSR/private mode */ }
}

// Read the e-ink flag applied to <html> by main.jsx at startup.
// Synchronous, cheap, safe to call from any render path.
export function isEinkActive() {
  return document.documentElement.getAttribute('data-eink') === 'true';
}

// E-ink: instant scroll avoids ghosting trails. Others: smooth animation.
export function scrollBehavior() {
  return isEinkActive() ? 'auto' : 'smooth';
}

// Paged mode is enabled on detected e-ink displays. CSS multicol + swipe
// gesture (in useHighlightFlow.js) gives a single instant scrollTo per
// page turn — exactly one full e-ink refresh, no ghosting from continuous
// drag. Native horizontal drag is suppressed via `touch-action: pan-y` in
// Reader.css so users can only advance pages by deliberate swipe, not
// by gradual horizontal scroll.
export function resolvePagedModeActive({ viewMode = 'chapter' } = {}) {
  if (viewMode !== 'chapter') return false;
  return detectEink();
}
